import { c as _c } from "react/compiler-runtime";
import { FARM_PAGE_CONFIG } from "@/constants/project-specific/farms";
import type { FarmConfig } from "@/constants/project-specific/farms/types";
import { getMasterChefConfigBySlug } from "@/util/contracts/masterchef";
import { useEffect, useState } from "react";
import { formatUnits } from "viem";
import { useConnection, useReadContracts } from "wagmi"; // useReadContract removed
import { type RequiredFarmChainType, fallbackChainId } from "../constants";

// Hook to get public data of a farm
export function useFarmUserData(farmConfig, t0) {
  const $ = _c(30);
  const chainId = t0 === undefined ? fallbackChainId : t0;
  const {
    address
  } = useConnection();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = BigInt(0);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [totalStaked, setTotalStaked] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = BigInt(0);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [pendingRewards, setPendingRewards] = useState(t2);
  const [isOwner, setIsOwner] = useState(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = BigInt(0);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [perBlockEmission, setPerBlockEmission] = useState(t3);
  const masterchef = getMasterChefConfigBySlug(farmConfig.masterChefSlug ?? FARM_PAGE_CONFIG.fallbackMasterchefSlug, chainId) ?? FARM_PAGE_CONFIG.defaultChef[fallbackChainId];
  const mcContract = {
    address: masterchef.address,
    abi: masterchef.abi
  } as const;
  const dynamicContracts = [{
    ...mcContract,
    functionName: "userInfo",
    args: [farmConfig.pid, address],
    chainId: masterchef.chainId
  }, {
    ...mcContract,
    functionName: masterchef.pendingFunction,
    args: [farmConfig.pid, address],
    chainId: masterchef.chainId
  }] as const;
  const staticContracts = [{
    ...mcContract,
    functionName: "owner",
    chainId: masterchef.chainId
  }, {
    ...mcContract,
    functionName: masterchef.rewardPerBlockFunction,
    chainId: masterchef.chainId
  }] as const;
  const {
    data: dynamicMcData,
    refetch: refetchDynamic
  } = useReadContracts({
    allowFailure: false,
    contracts: dynamicContracts,
    query: {
      enabled: !!address,
      refetchInterval: 100000,
      refetchOnWindowFocus: true
    }
  });
  const {
    data: staticMcData,
    refetch: refetchStatic
  } = useReadContracts({
    allowFailure: false,
    contracts: staticContracts,
    query: {
      refetchInterval: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  });
  let t4;
  let t5;
  if ($[3] !== address || $[4] !== dynamicMcData || $[5] !== staticMcData) {
    t4 = () => {
      if (!address) {
        return;
      }
      if (dynamicMcData) {
        const [stakedTotal] = dynamicMcData[0] as [bigint, bigint];
        setTotalStaked(stakedTotal);
        setPendingRewards(dynamicMcData[1] as bigint);
      }
      if (staticMcData) {
        setPerBlockEmission(staticMcData[1] as bigint);
      }
    };
    t5 = [address, dynamicMcData, staticMcData];
    $[3] = address;
    $[4] = dynamicMcData;
    $[5] = staticMcData;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== address || $[9] !== staticMcData) {
    t6 = () => {
      if (!address) {
        setIsOwner(false);
        return;
      }
      if (staticMcData && staticMcData[0]) {
        if (String(staticMcData[0]).toLowerCase() === String(address).toLowerCase()) {
          setIsOwner(true);
        } else {
          setIsOwner(false);
        }
      }
    };
    t7 = [address, staticMcData];
    $[8] = address;
    $[9] = staticMcData;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== refetchDynamic || $[13] !== refetchStatic) {
    t8 = async () => {
      await refetchDynamic();
      await refetchStatic();
    };
    $[12] = refetchDynamic;
    $[13] = refetchStatic;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const refresh = t8;
  const t9 = Number(totalStaked) / 1000000000000000000;
  let t10;
  if ($[15] !== t9 || $[16] !== totalStaked) {
    t10 = {
      formatted: t9,
      bigInt: totalStaked
    };
    $[15] = t9;
    $[16] = totalStaked;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = Number(pendingRewards) / 1000000000000000000;
  let t12;
  if ($[18] !== pendingRewards || $[19] !== t11) {
    t12 = {
      formatted: t11,
      bigInt: pendingRewards
    };
    $[18] = pendingRewards;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = formatUnits(perBlockEmission as bigint, masterchef.rewardToken.decimals);
  let t14;
  if ($[21] !== perBlockEmission || $[22] !== t13) {
    t14 = {
      formatted: t13,
      bigInt: perBlockEmission
    };
    $[21] = perBlockEmission;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== isOwner || $[25] !== refresh || $[26] !== t10 || $[27] !== t12 || $[28] !== t14) {
    t15 = {
      totalStaked: t10,
      pendingRewards: t12,
      emissionsPerBlock: t14,
      isOwner,
      refresh
    };
    $[24] = isOwner;
    $[25] = refresh;
    $[26] = t10;
    $[27] = t12;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}